import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";

import PanelCard from "layouts/dutyEstimator/components/PanelCard";
import dutyRateTableData from "layouts/dutyEstimator/data/dutyRateTableData";
import {
  CONDITION_OPTIONS,
  FUEL_TYPE_OPTIONS,
  DUTY_RATE_BODY_TYPE_OPTIONS,
  INPUT_SX,
  WHITE_BUTTON_SX,
  GREEN_BUTTON_SX,
} from "layouts/dutyEstimator/constants";

const EMPTY_FORM = {
  hsCode: "",
  category: "",
  condition: "any",
  fuelType: "any",
  bodyType: "",
  engineCcMin: "",
  engineCcMax: "",
  dutyRate: "",
  description: "",
  isActive: true,
};

function DutyRatesPanel({ dutyRates, categoryOptions, onSave, onDelete, onValidationError }) {
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const handleAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const handleEdit = useCallback((rate) => {
    setEditingId(rate?.id);
    setForm({
      hsCode: rate?.hsCode ?? "",
      category: rate?.category ?? "",
      condition: rate?.condition ?? "any",
      fuelType: rate?.fuelType ?? "any",
      bodyType: rate?.bodyType ?? "",
      engineCcMin: rate?.engineCcMin ?? "",
      engineCcMax: rate?.engineCcMax ?? "",
      dutyRate: rate?.dutyRate ?? "",
      description: rate?.description ?? "",
      isActive: rate?.isActive !== false,
    });
    setFormOpen(true);
  }, []);

  const handleSubmit = async () => {
    if (!form.category || form.dutyRate === "") {
      onValidationError("Please choose a category and enter a duty rate.");
      return;
    }
    if (form.engineCcMin !== "" && form.engineCcMax !== "" && Number(form.engineCcMin) > Number(form.engineCcMax)) {
      onValidationError("Minimum engine capacity cannot be greater than the maximum.");
      return;
    }

    const saved = await onSave(editingId, {
      category: form.category,
      condition: form.condition,
      fuelType: form.fuelType,
      bodyType: form.bodyType || null,
      dutyRate: Number(form.dutyRate),
      // Blank means "no bound", which the API expects as null rather than an empty string.
      engineCcMin: form.engineCcMin === "" ? null : Number(form.engineCcMin),
      engineCcMax: form.engineCcMax === "" ? null : Number(form.engineCcMax),
      hsCode: form.hsCode || null,
      description: form.description || null,
      isActive: form.isActive,
    });
    if (saved) setFormOpen(false);
  };

  const { columns, rows } = useMemo(
    () => dutyRateTableData({ dutyRateList: dutyRates, onEdit: handleEdit, onDelete }),
    [dutyRates, handleEdit, onDelete]
  );

  return (
    <>
      <PanelCard
        title="Duty Rates"
        caption="Keyed by HS code, category, fuel and engine size. When several rows match a vehicle, the most specific one wins."
        action={
          <Button variant="contained" size="small" onClick={handleAdd} sx={WHITE_BUTTON_SX}>
            Add Duty Rate
          </Button>
        }
      >
        <MDBox pt={3}>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: 10, entries: [10, 15, 25, 50] }}
            showTotalEntries
            noEndBorder
            canSearch
            page={page}
            onPageChange={setPage}
          />
        </MDBox>
      </PanelCard>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{editingId ? "Edit Duty Rate" : "Add Duty Rate"}</DialogTitle>
        <DialogContent>
          <MDTypography variant="caption" color="text" display="block" mb={2}>
            Leave condition, fuel and engine size as-is for a catch-all rate. Narrow them to create an
            override — electric vehicles at 0%, for instance.
          </MDTypography>

          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                autoFocus
                fullWidth
                label="Category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                sx={INPUT_SX}
                SelectProps={{ IconComponent: ArrowDropDownIcon }}
              >
                {categoryOptions.length === 0 ? (
                  <MenuItem disabled value="">
                    No categories available
                  </MenuItem>
                ) : (
                  categoryOptions.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="HS Code (optional)"
                value={form.hsCode}
                onChange={(e) => setForm({ ...form, hsCode: e.target.value })}
                helperText="A quote naming this code uses this row directly."
                sx={INPUT_SX}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Condition"
                value={form.condition}
                onChange={(e) => setForm({ ...form, condition: e.target.value })}
                sx={INPUT_SX}
                SelectProps={{ IconComponent: ArrowDropDownIcon }}
              >
                {CONDITION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Fuel Type"
                value={form.fuelType}
                onChange={(e) => setForm({ ...form, fuelType: e.target.value })}
                sx={INPUT_SX}
                SelectProps={{ IconComponent: ArrowDropDownIcon }}
              >
                {FUEL_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Body Type"
                value={form.bodyType}
                onChange={(e) => setForm({ ...form, bodyType: e.target.value })}
                helperText="Blank = applies to any body type in this category"
                sx={INPUT_SX}
                SelectProps={{ IconComponent: ArrowDropDownIcon }}
              >
                <MenuItem value="">Any body type</MenuItem>
                {DUTY_RATE_BODY_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Engine (cc)"
                value={form.engineCcMin}
                onChange={(e) => setForm({ ...form, engineCcMin: e.target.value })}
                helperText="Blank = no lower limit"
                sx={INPUT_SX}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Engine (cc)"
                value={form.engineCcMax}
                onChange={(e) => setForm({ ...form, engineCcMax: e.target.value })}
                helperText="Blank = no upper limit"
                sx={INPUT_SX}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Duty Rate (%)"
                value={form.dutyRate}
                onChange={(e) => setForm({ ...form, dutyRate: e.target.value })}
                sx={INPUT_SX}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <MDBox display="flex" alignItems="center" height="100%">
                <FormControlLabel
                  control={
                    <Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                  }
                  label={
                    <MDTypography variant="button" fontWeight="regular">
                      Active
                    </MDTypography>
                  }
                />
              </MDBox>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (optional)"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                sx={INPUT_SX}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} sx={GREEN_BUTTON_SX}>
            {editingId ? "Save Changes" : "Add Duty Rate"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

DutyRatesPanel.propTypes = {
  dutyRates: PropTypes.array.isRequired,
  categoryOptions: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onValidationError: PropTypes.func.isRequired,
};

export default DutyRatesPanel;
